import { useEffect, useMemo, useState } from "react";
import AMapLoader from "@amap/amap-jsapi-loader";
import { Button } from "@/components/ui/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible";
import { TripMapPreview } from "@/components/map/TripMapPreview";
import type { RouteInfo } from "@/components/map/AmapComponets";
import { ChevronsUpDown, MapPin } from "lucide-react";

interface MapCardPoint {
  loc: {
    x: number;
    y: number;
  };
  info: string;
}

interface MapCardProps {
  data?: MapCardPoint[];
}

type PointAddress = {
  city?: string;
  district?: string;
  formattedAddress?: string;
  adcode?: string;
};

type WeatherInfo = {
  weather: string;
  temperature: string;
  humidity: string;
  windDirection: string;
  windPower: string;
  reportTime: string;
};

export function MapCard({ data }: MapCardProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [pointAddresses, setPointAddresses] = useState<
    Record<number, PointAddress>
  >({});
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [weatherByIndex, setWeatherByIndex] = useState<Record<number, WeatherInfo>>({});

  const points = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    if (points.length === 0) {
      setPointAddresses({});
      setWeatherByIndex({});
      return;
    }

    let cancelled = false;

    // @ts-ignore
    window._AMapSecurityConfig = {
      securityJsCode: "e39abc8cafb4e2f42ad123c42e46fed1",
    };

    AMapLoader.load({
      key: import.meta.env.VITE_AMAP_KEY,
      version: "2.0",
      plugins: ["AMap.Geocoder"],
    })
      .then((AMap) => {
        if (cancelled) return;

        const geocoder = new AMap.Geocoder({ city: "全国" });

        points.forEach((point, index) => {
          geocoder.getAddress(
            [point.loc.x, point.loc.y],
            (status: string, result: any) => {
              if (cancelled || status !== "complete" || !result?.regeocode) {
                return;
              }

              const comp = result.regeocode.addressComponent || {};
              const city =
                typeof comp.city === "string" && comp.city
                  ? comp.city
                  : comp.province;

              setPointAddresses((prev) => ({
                ...prev,
                [index]: {
                  city,
                  district: comp.district,
                  formattedAddress: result.regeocode.formattedAddress,
                  adcode: comp.adcode,
                },
              }));
            },
          );
        });
      })
      .catch((error) => {
        console.error("地址解析初始化失败:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [points]);

  useEffect(() => {
    const entries = Object.entries(pointAddresses).filter(([, value]) => value?.adcode);
    if (entries.length === 0) {
      setWeatherByIndex({});
      return;
    }

    let cancelled = false;

    // @ts-ignore
    window._AMapSecurityConfig = {
      securityJsCode: "e39abc8cafb4e2f42ad123c42e46fed1",
    };

    AMapLoader.load({
      key: import.meta.env.VITE_AMAP_KEY,
      version: "2.0",
      plugins: ["AMap.Weather"],
    })
      .then((AMap) => {
        if (cancelled) return;

        const weather = new AMap.Weather();

        entries.forEach(([indexStr, value]) => {
          const index = Number(indexStr);
          if (weatherByIndex[index]) return;

          weather.getLive(value.adcode, (_err: any, data: any) => {
            if (cancelled || !data) return;

            setWeatherByIndex((prev) => ({
              ...prev,
              [index]: {
                weather: data.weather || "-",
                temperature: data.temperature || "-",
                humidity: data.humidity || "-",
                windDirection: data.windDirection || data.winddirection || "-",
                windPower: data.windPower || data.windpower || "-",
                reportTime: data.reportTime || data.reporttime || "-",
              },
            }));
          });
        });
      })
      .catch(() => {
        if (!cancelled) {
          setWeatherByIndex({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pointAddresses]);

  const start = points[0];
  const end = points.length > 1 ? points[points.length - 1] : undefined;

  const startPoint = useMemo(
    () =>
      start
        ? {
            lng: start.loc.x,
            lat: start.loc.y,
          }
        : undefined,
    [start],
  );

  const endPoint = useMemo(
    () =>
      end
        ? {
            lng: end.loc.x,
            lat: end.loc.y,
          }
        : undefined,
    [end],
  );

  const routePath = useMemo(
    () =>
      points.map((point) => ({
        lng: point.loc.x,
        lat: point.loc.y,
      })),
    [points],
  );

  const estimatedDurationText = useMemo(() => {
    if (!routeInfo?.duration) return "-";

    const minutes = Math.round(routeInfo.duration / 60);
    if (minutes < 60) return `${minutes} 分钟`;

    const hours = Math.floor(minutes / 60);
    const remainMinutes = minutes % 60;
    return remainMinutes > 0
      ? `${hours} 小时 ${remainMinutes} 分钟`
      : `${hours} 小时`;
  }, [routeInfo]);

  if (points.length === 0) {
    return null;
  }

  return (
    <div className="w-full border rounded-xl overflow-hidden bg-white">
      <div className="h-80 w-full relative">
        <TripMapPreview
          startPoint={startPoint}
          endPoint={endPoint}
          routePath={routePath}
          onRouteInfoChange={setRouteInfo}
        />
      </div>

      <div className="p-4 bg-slate-50/60 border-t border-border space-y-3">
        <div className="text-sm font-semibold text-slate-800">
          {points.length === 1 ? "地理坐标展示" : "路线与途径点展示"}
        </div>

        {points.length > 1 && (
          <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-md px-3 py-2 space-y-1">
            <div>
              路径信息：预计里程约 {(
                (routeInfo?.distance ?? 0) / 1000
              ).toFixed(2)} km
            </div>
            <div>预估时间：{estimatedDurationText}</div>
          </div>
        )}

        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
          className="w-full border rounded-lg bg-white"
        >
          <div className="flex items-center justify-between px-4 py-3">
            <div className="text-sm font-medium text-slate-700">
              坐标点详情（{points.length}）
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <ChevronsUpDown className="h-4 w-4 text-slate-500" />
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent>
            <div className="px-4 pb-3 space-y-2">
              {points.map((point, index) => (
                <div
                  key={`${point.info}-${index}`}
                  className="flex items-start gap-2 text-xs p-2 rounded bg-slate-50 border border-slate-100"
                >
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-slate-700">{point.info}</span>
                    <span className="text-slate-600 leading-tight">
                      {pointAddresses[index]?.city
                        ? `${pointAddresses[index].city}${
                            pointAddresses[index].district
                              ? ` · ${pointAddresses[index].district}`
                              : ""
                          }`
                        : "地址解析中..."}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      {pointAddresses[index]?.formattedAddress || "-"}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      经度：{point.loc.x}，纬度：{point.loc.y}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      天气：
                      {weatherByIndex[index]
                        ? `${weatherByIndex[index].weather} ${weatherByIndex[index].temperature}℃`
                        : "加载中..."}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      风向：
                      {weatherByIndex[index]
                        ? `${weatherByIndex[index].windDirection}风 ${weatherByIndex[index].windPower}级`
                        : "加载中..."}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      湿度：
                      {weatherByIndex[index]
                        ? `${weatherByIndex[index].humidity}%`
                        : "加载中..."}
                    </span>
                    <span className="text-slate-500 leading-tight">
                      更新时间：
                      {weatherByIndex[index]
                        ? weatherByIndex[index].reportTime
                        : "加载中..."}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
}
